import { ECOLOGICAL_SIGN } from "@/constants/editor";

let uuidSeq = 0;
let uuidLastMs = 0;

/**
 * 组件 id：纯数字字符串，不经过 Number。
 * 时间戳拼进 Number 后超过安全整数会丢精度，复制出的按钮 id 撞号，画布不再单独渲染。
 */
export function uuid() {
  const ms = Date.now();
  if (ms > uuidLastMs) {
    uuidLastMs = ms;
    uuidSeq = 0;
  }
  uuidSeq += 1;
  if (uuidSeq >= 1000) {
    uuidLastMs += 1;
    uuidSeq = 1;
  }
  return String(uuidLastMs) + String(uuidSeq).padStart(3, "0");
}

/** 已有数字 id 收成十进制字符串，不改 "Back" 和非数字 id */
export function numericIdToString(val) {
  if (val == null || val === "" || val === "Back") {
    return val;
  }
  if (typeof val === "number" && Number.isFinite(val)) {
    return String(val);
  }
  if (typeof val === "string" && /^-?\d+(\.\d+)?$/.test(val.trim())) {
    const trimmed = val.trim();
    if (trimmed.indexOf(".") === -1) {
      return trimmed;
    }
    const n = Number(trimmed);
    if (Number.isFinite(n)) {
      return String(n);
    }
  }
  return val;
}

/** 组件 id、以及指向组件 id 的事件/联动字段，统一成字符串 */
export function stringifyComponentIds(list) {
  const seen = new Set();
  const walk = (node) => {
    if (!node || typeof node !== "object" || seen.has(node)) {
      return;
    }
    seen.add(node);
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (node.component && node.id != null && node.id !== "Back") {
      node.id = numericIdToString(node.id);
    }
    if (node.parentid != null) {
      node.parentid = numericIdToString(node.parentid);
    }
    if (node.parentId != null) {
      node.parentId = numericIdToString(node.parentId);
    }
    const events = node.events;
    if (events && typeof events === "object") {
      if (events.clickEvent && typeof events.clickEvent === "object") {
        events.clickEvent.comp = numericIdToString(events.clickEvent.comp);
      }
      ["mutuallyEvent", "jumpEvent"].forEach((key) => {
        if (Array.isArray(events[key])) {
          events[key] = events[key].map(numericIdToString);
        }
      });
      ["pressEvent", "pressAgainEvent", "instructionDatas"].forEach((key) => {
        if (!Array.isArray(events[key])) {
          return;
        }
        events[key].forEach((item) => {
          if (item && typeof item === "object") {
            item.id = numericIdToString(item.id);
          }
        });
      });
    }
    if (node.componentLinks && Array.isArray(node.componentLinks.links)) {
      node.componentLinks.links.forEach((link) => {
        if (!link || typeof link !== "object") {
          return;
        }
        link.id = numericIdToString(link.id);
        link.comp = numericIdToString(link.comp);
        if (Array.isArray(link.changeAttrs)) {
          link.changeAttrs.forEach((attr) => {
            if (attr && typeof attr === "object") {
              attr.id = numericIdToString(attr.id);
            }
          });
        }
      });
    }
    if (Array.isArray(node.propValue)) {
      walk(node.propValue);
    }
  };
  walk(list);
  return list;
}

export function collectUsedComponentIds(list, used) {
  (list || []).forEach((node) => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (node.id != null && node.id !== "Back") {
      used.add(String(node.id));
    }
    if (Array.isArray(node.propValue)) {
      collectUsedComponentIds(node.propValue, used);
    }
  });
}

function nextUniqueComponentId(used) {
  for (let guard = 0; guard < 50; guard += 1) {
    const id = uuid();
    if (!used.has(String(id))) {
      used.add(String(id));
      return id;
    }
  }
  const id = uuid();
  used.add(String(id));
  return id;
}

/** 粘贴/复制：深拷贝并递归换新 id（Group / 生态子节点 parentId 一并修正） */
export function reassignComponentTreeIds(comp, used, deepCopyFn) {
  const c = deepCopyFn(comp);
  const regenNode = (node) => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (node.id != null && node.id !== "Back") {
      node.id = nextUniqueComponentId(used);
    }
    const parentId = node.id;
    if (!Array.isArray(node.propValue)) {
      return;
    }
    if (node.component === "Group") {
      node.propValue.forEach((child) => {
        if (!child || typeof child !== "object") {
          return;
        }
        regenNode(child);
        if (parentId != null) {
          child.parentId = parentId;
        }
      });
      return;
    }
    if (node.compType === ECOLOGICAL_SIGN) {
      node.propValue.forEach((child) => {
        if (!child || typeof child !== "object") {
          return;
        }
        if (child.id != null && child.id !== "Back") {
          child.id = nextUniqueComponentId(used);
        }
        if (parentId != null) {
          child.parentid = parentId;
        }
      });
      return;
    }
    node.propValue.forEach(regenNode);
  };
  regenNode(c);
  return c;
}

/** 加载/保存后：同一页出现重复 id 时，为后出现的节点换新 id（修复历史 Number 精度撞号） */
export function resolveDuplicateIdsOnPage(list) {
  if (!Array.isArray(list)) {
    return list;
  }
  const seen = new Set();
  const used = new Set();
  const fixNode = (node) => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (node.id != null && node.id !== "Back") {
      const sid = String(node.id);
      if (seen.has(sid)) {
        node.id = nextUniqueComponentId(used);
      } else {
        seen.add(sid);
        used.add(sid);
      }
    }
    if (Array.isArray(node.propValue)) {
      node.propValue.forEach(fixNode);
    }
  };
  list.forEach(fixNode);
  return list;
}

export function ensureComponentTreeIdsNotUsed(component, used) {
  if (!component || typeof component !== "object") {
    return;
  }
  const sid =
    component.id != null && component.id !== "Back"
      ? String(component.id)
      : "";
  if (sid && used.has(sid)) {
    reassignComponentTreeIds(component, used, (x) => x);
  } else {
    collectUsedComponentIds([component], used);
  }
}
